import { safeNumber } from '../../lib/number';
import type { Candle, ChartInterval } from '../../types/market';

interface BinanceKlinePayload {
  E?: number;
  e?: string;
  k?: {
    t: number;
    T: number;
    s: string;
    i: string;
    o: string;
    h: string;
    l: string;
    c: string;
    v: string;
    q?: string;
    n?: number;
    x: boolean;
  };
}

export interface BinanceKlineUpdate {
  candle: Candle;
  eventTime: number;
}

export type KlineStreamState = 'connecting' | 'live' | 'reconnecting' | 'unavailable' | 'closed';

export interface KlineStreamStatus {
  state: KlineStreamState;
  message: string;
  retries: number;
  endpoint?: string;
}

interface SubscribeKlineOptions {
  onStatus?: (status: KlineStreamStatus) => void;
}

function streamEndpoints(marketKind: 'spot' | 'futures'): string[] {
  return marketKind === 'futures'
    ? ['wss://fstream.binance.com/market/ws', 'wss://fstream.binance.com/ws']
    : ['wss://data-stream.binance.vision:443/ws', 'wss://stream.binance.com:9443/ws'];
}

export function subscribeBinanceKline(
  symbol: string,
  interval: ChartInterval,
  marketKind: 'spot' | 'futures',
  onUpdate: (update: BinanceKlineUpdate) => void,
  options: SubscribeKlineOptions = {},
): () => void {
  let stopped = false;
  let socket: WebSocket | undefined;
  let retryTimer: number | undefined;
  let retries = 0;
  let endpointIndex = 0;
  let validEvents = 0;
  const streamName = `${symbol.toLowerCase()}@kline_${interval}`;
  const endpoints = streamEndpoints(marketKind);

  const publish = (state: KlineStreamState, message: string, endpoint?: string): void => {
    options.onStatus?.({ state, message, retries, ...(endpoint ? { endpoint } : {}) });
  };

  const scheduleReconnect = (): void => {
    if (stopped) return;
    retries += 1;
    endpointIndex = (endpointIndex + 1) % endpoints.length;
    const delay = Math.min(20_000, 750 * 2 ** Math.min(retries, 5));
    publish('reconnecting', `Live ${interval} stream reconnecting in ${(delay / 1_000).toFixed(1)}s.`, endpoints[endpointIndex]);
    retryTimer = window.setTimeout(connect, delay);
  };

  const connect = (): void => {
    if (stopped) return;
    const endpoint = endpoints[endpointIndex]!;
    publish(retries > 0 ? 'reconnecting' : 'connecting', `Connecting live ${interval} candle stream.`, endpoint);
    socket = new WebSocket(`${endpoint}/${streamName}`);

    const openTimeout = window.setTimeout(() => {
      if (socket?.readyState !== WebSocket.OPEN) socket?.close();
    }, 8_000);

    socket.addEventListener('open', () => {
      window.clearTimeout(openTimeout);
      publish('connecting', `Candle socket connected; awaiting the first verified ${interval} market event.`, endpoint);
    });

    socket.addEventListener('message', (event) => {
      try {
        const payload = JSON.parse(String(event.data)) as BinanceKlinePayload;
        const kline = payload.k;
        if (!kline || kline.s !== symbol || kline.i !== interval) return;
        const firstValidEvent = validEvents === 0;
        validEvents += 1;
        retries = 0;
        if (firstValidEvent) publish('live', `Live ${interval} candle events verified.`, endpoint);
        onUpdate({
          candle: {
            openTime: safeNumber(kline.t),
            closeTime: safeNumber(kline.T),
            open: safeNumber(kline.o),
            high: safeNumber(kline.h),
            low: safeNumber(kline.l),
            close: safeNumber(kline.c),
            volume: safeNumber(kline.v),
            quoteVolume: safeNumber(kline.q),
            trades: safeNumber(kline.n),
            closed: Boolean(kline.x),
          },
          eventTime: safeNumber(payload.E, Date.now()),
        });
      } catch (error) {
        publish(
          validEvents > 0 ? 'live' : 'connecting',
          `A malformed market event was ignored: ${error instanceof Error ? error.message : 'invalid payload'}.`,
          endpoint,
        );
      }
    });

    socket.addEventListener('close', () => {
      window.clearTimeout(openTimeout);
      if (stopped) return;
      if (validEvents === 0 && retries >= 7) {
        publish('unavailable', `Live ${interval} stream is unavailable after repeated endpoint attempts.`, endpoint);
        return;
      }
      scheduleReconnect();
    });

    socket.addEventListener('error', () => socket?.close());
  };

  connect();
  return () => {
    stopped = true;
    if (retryTimer !== undefined) window.clearTimeout(retryTimer);
    socket?.close();
  };
}
